import { useState, useEffect, useCallback, useRef } from 'react';
import { drivingTipAPI, uploadAPI, BASE_URL } from '../services/api';

const styles = {
  container: { width: '100%' },
  header: { marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title: { fontSize: '24px', fontWeight: '700', color: '#1e293b', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', marginTop: '4px' },
  card: { backgroundColor: '#ffffff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
  toolbar: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '16px', borderBottom: '1px solid #e2e8f0' },
  addButton: { padding: '10px 20px', backgroundColor: '#4f46e5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' },
  td: { padding: '16px', borderBottom: '1px solid #f1f5f9', fontSize: '14px', color: '#1e293b', verticalAlign: 'middle' },
  thumb: { width: '64px', height: '64px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e2e8f0' },
  statusBadge: { padding: '4px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', cursor: 'pointer' },
  activeStatus: { backgroundColor: '#dcfce7', color: '#166534' },
  inactiveStatus: { backgroundColor: '#fee2e2', color: '#991b1b' },
  actionButtons: { display: 'flex', gap: '8px' },
  iconButton: { padding: '8px', border: 'none', borderRadius: '6px', backgroundColor: 'transparent', cursor: 'pointer', color: '#64748b' },
  emptyState: { textAlign: 'center', padding: '60px 20px', color: '#64748b' },
  modal: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modalContent: { backgroundColor: '#fff', borderRadius: '16px', width: '90%', maxWidth: '500px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  modalHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1e293b' },
  modalBody: { padding: '24px', overflowY: 'auto', flex: 1 },
  modalFooter: { padding: '16px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '12px' },
  formGroup: { marginBottom: '16px' },
  label: { display: 'block', fontSize: '13px', fontWeight: '500', color: '#475569', marginBottom: '6px' },
  input: { width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
  imageUploadBox: { border: '2px dashed #cbd5e1', borderRadius: '8px', padding: '24px', textAlign: 'center', cursor: 'pointer', transition: 'all 0.15s', backgroundColor: '#f8fafc' },
  imagePreview: { display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '8px' },
  button: { padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer', border: 'none' },
  primaryButton: { backgroundColor: '#4f46e5', color: '#fff' },
  secondaryButton: { backgroundColor: '#f1f5f9', color: '#475569' },
  errorBox: { padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', color: '#dc2626', marginBottom: '16px', fontSize: '14px' },
  checkboxRow: { display: 'flex', alignItems: 'center', gap: '8px' },
};

const DrivingTips = () => {
  const [tips, setTips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({ image: '', link: '', sortOrder: 0, isActive: true });
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchTips = useCallback(async () => {
    try {
      setLoading(true);
      const res = await drivingTipAPI.getAll();
      setTips(res.data.data || []);
    } catch { setError('Failed to fetch driving tips'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTips(); }, [fetchTips]);

  const openCreate = () => {
    setEditing(null);
    setFormData({ image: '', link: '', sortOrder: tips.length + 1, isActive: true });
    setFormError(''); setShowModal(true);
  };

  const openEdit = (tip) => {
    setEditing(tip);
    setFormData({ image: tip.image, link: tip.link, sortOrder: tip.sortOrder, isActive: tip.isActive });
    setFormError(''); setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setFormError('');
    try {
      const res = await uploadAPI.uploadTipImage(file);
      if (res.data?.url) setFormData((prev) => ({ ...prev, image: res.data.url }));
    } catch { setFormError('Failed to upload image'); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!formData.image || !formData.link) { setFormError('Image and link are required'); return; }
    try {
      setSaving(true);
      if (editing) await drivingTipAPI.update(editing.id, formData);
      else await drivingTipAPI.create(formData);
      setShowModal(false); fetchTips();
    } catch (e) { setFormError(e.response?.data?.error || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleToggle = async (tip) => {
    try { await drivingTipAPI.toggleActive(tip.id); fetchTips(); }
    catch { setError('Failed to toggle'); }
  };

  const handleDelete = async (tip) => {
    if (!window.confirm('Delete this tip?')) return;
    try { await drivingTipAPI.delete(tip.id); fetchTips(); }
    catch { setError('Failed to delete'); }
  };

  const imgUrl = (path) => path.startsWith('http') ? path : `${BASE_URL}${path}`;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Driving Tips</h1>
          <p style={styles.subtitle}>Clickable image cards shown below the traffic rules table</p>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.card}>
        <div style={styles.toolbar}>
          <button style={styles.addButton} onClick={openCreate}>
            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path d="M12 4v16m-8-8h16" /></svg>
            Add Tip
          </button>
        </div>

        {loading ? (
          <div style={styles.emptyState}>Loading...</div>
        ) : tips.length === 0 ? (
          <div style={styles.emptyState}>No driving tips yet.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={{ ...styles.th, width: '60px' }}>Order</th>
                <th style={{ ...styles.th, width: '90px' }}>Image</th>
                <th style={styles.th}>Link</th>
                <th style={{ ...styles.th, width: '100px' }}>Status</th>
                <th style={{ ...styles.th, width: '100px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tips.map((tip) => (
                <tr key={tip.id}>
                  <td style={styles.td}>{tip.sortOrder}</td>
                  <td style={styles.td}>
                    <img src={imgUrl(tip.image)} alt="" style={styles.thumb} />
                  </td>
                  <td style={styles.td}>
                    <a href={tip.link} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', wordBreak: 'break-all', fontSize: '13px' }}>{tip.link}</a>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, ...(tip.isActive ? styles.activeStatus : styles.inactiveStatus) }} onClick={() => handleToggle(tip)}>
                      {tip.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button style={styles.iconButton} onClick={() => openEdit(tip)} title="Edit">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                      </button>
                      <button style={{ ...styles.iconButton, color: '#dc2626' }} onClick={() => handleDelete(tip)} title="Delete">
                        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div style={styles.modal} onClick={(e) => e.target === e.currentTarget && setShowModal(false)}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{editing ? 'Edit Driving Tip' : 'Add Driving Tip'}</h2>
              <button style={styles.iconButton} onClick={() => setShowModal(false)}>
                <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div style={styles.modalBody}>
              {formError && <div style={styles.errorBox}>{formError}</div>}

              <div style={styles.formGroup}>
                <label style={styles.label}>Image *</label>
                {formData.image ? (
                  <div style={styles.imagePreview}>
                    <img src={imgUrl(formData.image)} alt="" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '6px' }} />
                    <div style={{ flex: 1, fontSize: '12px', color: '#64748b', wordBreak: 'break-all' }}>{formData.image}</div>
                    <button type="button" style={{ ...styles.button, ...styles.secondaryButton, padding: '6px 12px', fontSize: '12px' }} onClick={() => setFormData((prev) => ({ ...prev, image: '' }))}>Remove</button>
                  </div>
                ) : (
                  <div style={styles.imageUploadBox} onClick={() => fileInputRef.current?.click()}>
                    <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} disabled={uploading} style={{ display: 'none' }} />
                    {uploading ? (
                      <div style={{ fontSize: '13px', color: '#64748b' }}>Uploading...</div>
                    ) : (
                      <>
                        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" style={{ margin: '0 auto 8px' }}>
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <div style={{ fontSize: '13px', color: '#475569', fontWeight: '500' }}>Click to upload image</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '4px' }}>PNG, JPG, WebP up to 5MB</div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Link *</label>
                <input style={styles.input} type="text" value={formData.link} onChange={(e) => setFormData({ ...formData, link: e.target.value })} placeholder="/car-insurance or https://..." />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Sort Order</label>
                <input style={styles.input} type="number" value={formData.sortOrder} onChange={(e) => setFormData({ ...formData, sortOrder: Number(e.target.value) })} />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.checkboxRow}>
                  <input type="checkbox" checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />
                  <span style={{ fontSize: '14px' }}>Active (visible on the website)</span>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={{ ...styles.button, ...styles.secondaryButton }} onClick={() => setShowModal(false)} disabled={saving}>Cancel</button>
              <button style={{ ...styles.button, ...styles.primaryButton, opacity: saving ? 0.6 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrivingTips;
